import { AuthService } from 'src/app/base/services/auth.service';
import { DataManager } from '@syncfusion/ej2-data';
import { Component, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClientService } from 'src/app/base/services/client/client.service';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { FleetListService } from './fleet-list.service';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { TitleBarComponent } from 'src/app/base/shared/title-bar/title-bar.component';
import { GridModule, SaveEventArgs } from '@syncfusion/ej2-angular-grids';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { SfCommandButtonType, SfRequestTypes } from 'src/app/base/models/syncfusion-data';
import { CommonModule } from '@angular/common';
import { ClientAutocompleteComponent } from 'src/app/base/shared/client-autocomplete/client-autocomplete.component';
import { AutocompleteComponent } from 'src/app/base/shared/autocomplete/autocomplete.component';
import { AutocompleteItem } from 'src/app/base/shared/autocomplete/autocomplete-item';
import { map, Observable, of } from 'rxjs';
import { ClientDTO, ControlPlateType, FleetListEntryDTO, FleetListEntryType, PagedResultDTOOfClientDTO, PagedResultDTOOfParkerCompanyDTO, ParkerCompanyDTO } from 'src/app/base/models/models.swagger.generated';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { EnumTranslationService } from 'src/app/base/services/enum-translation.service';
import { DropDownItemTyped } from 'src/app/base/shared/drop-down-item.model';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { CountryISO2 } from 'src/app/base/shared/countryISO2-enum';

@Component({
  selector: 'app-fleet-list',
  imports: [TitleBarComponent, GridModule, ToolbarModule, TranslateModule, CommonModule, ClientAutocompleteComponent, AutocompleteComponent, DropDownListModule],
  templateUrl: './fleet-list.component.html',
  styleUrl: './fleet-list.component.scss'
})
export class FleetListComponent extends GridBaseComponent implements OnInit {

  public keyValueDropdownSettings = { text: 'name', value: 'id' };
  public ControlPlateType = ControlPlateType;
  public FleetListEntryType = FleetListEntryType;
  public controlPlateTypeSelectionData: DropDownItemTyped<any>[];
  public typeSelectionData: DropDownItemTyped<any>[];
  public fields = { text: 'text', value: 'value' };
  public checkBoxFilter = GridConstantsFactory.createCheckBoxFilter();
  public countries: AutocompleteItem[] = [];
  public dropdownSettings = { text: 'name', value: 'id' };
  public controlPlateValidationRule?: object;

  public toolbar = ['Add', 'Update', 'Cancel'];
  public customEditSettings = { allowEditing: true, allowAdding: true, allowDeleting: true, mode: 'Normal', showDeleteConfirmDialog: true };
  public customGridCommands = [
    {
      type: SfCommandButtonType.Delete,
      buttonOption:
      {
        cssClass: 'icon-btn',
        iconCss: 'icon-trash-can e-icons e-danger'
      }
    }
  ];

  public override get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public override set selectedClient(value: ClientDTO) {
    if (this.clientService.selectedClient !== value) {
      this.clientService.selectedClient = value;
      this.onClientSelected(value);
      this.grid?.refresh();
    }
  }

  public get selectedCompany(): ParkerCompanyDTO {
    return this.company;
  }

  public set selectedCompany(value: ParkerCompanyDTO) {
    if (value != null && typeof value.id === 'number') {
      this.company = value;
      this.fleetListService.selectedCompanyId = value.id;
    } else {
      this.company = null;
      this.fleetListService.selectedCompanyId = null;
    }
    if (!this.companyInitialySelected) {
      this.grid?.refresh();
    }
  }

  public gridEditSelectedCompany: ParkerCompanyDTO = null;
  public gridEditSelectedCountryCode: CountryISO2 = CountryISO2.CH;
  public gridEditSelectedControlPlateType: ControlPlateType = null;
  public gridEditSelectedType: FleetListEntryType = null;

  public companyInitialySelected = false;
  private company: ParkerCompanyDTO = null;

  constructor(
    translateService: TranslateService,
    clientService: ClientService,
    private fleetListService: FleetListService,
    private spinnerService: SpinnerService,
    private enumTranslationService: EnumTranslationService,
    private authService: AuthService
  ) {
    super(clientService, translateService, fleetListService);
  }

  ngOnInit(): void {
    this.controlPlateValidationRule = { required: true, maxLength: 10, minLength: [this.controlPlateValidateFn, this.translateService.instant('i18n.validation.controlPlateInvalid')] }
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.fleetListService, this.spinnerService)
    });

    this.controlPlateTypeSelectionData = this.enumTranslationService.translateNumericEnum(ControlPlateType, "i18n.shared.controlPlateType.");
    this.typeSelectionData = this.enumTranslationService.translateNumericEnum(FleetListEntryType, "i18n.shared.fleet-list-entry-type.");

    if (this.authService.isUserCompanyAdmin()) {
      this.companyInitialySelected = true;
      this.selectedCompany = this.authService.getUserInfo().company;
    }

    for (const country in CountryISO2) {
      this.countries.push({ id: country, name: this.translateService.instant('i18n.shared.CountryISO2.' + country) });
    }
  }

  public searchClientAutocompleteValues(searchText: string): Observable<AutocompleteItem[]> {
    return this.clientService.searchClients(searchText, 20)
      .pipe(map((clientPaged: PagedResultDTOOfClientDTO) => {
        const data: AutocompleteItem[] = [];
        clientPaged.items.forEach(client => {
          data.push({ id: client.id, name: client.name });
        });
        return data;
      }));
  }

  public searchCompanyAutocompleteValues(searchText: string): Observable<AutocompleteItem[]> {
    return this.fleetListService.searchCompanyDropdownValues(this.selectedClient.id, searchText)
      .pipe(map((companyDropdownValues: PagedResultDTOOfParkerCompanyDTO) => {
        const data: AutocompleteItem[] = [];
        companyDropdownValues.items.forEach(customer => {
          data.push({ id: customer.id, name: customer.name });
        });
        return data;
      }));
  }

  public override actionBegin(args: SaveEventArgs) {
    if (args.requestType === SfRequestTypes.Add) {
      // Set default values
      (args.data as FleetListEntryDTO).client = this.selectedClient;
      if (this.selectedCompany) {
        this.gridEditSelectedCompany = this.selectedCompany;
      } else {
        this.gridEditSelectedCompany = null;
      }
      this.gridEditSelectedCountryCode = CountryISO2.CH;
      this.gridEditSelectedControlPlateType = ControlPlateType.Car;
      this.gridEditSelectedType = FleetListEntryType.FleetVehicle;
    } else if (args.requestType === SfRequestTypes.Save) {
      (args.data as FleetListEntryDTO).clientId = (args.data as FleetListEntryDTO).client?.id;
      (args.data as FleetListEntryDTO).company = this.gridEditSelectedCompany;
      this.gridEditSelectedCompany = null;
      (args.data as FleetListEntryDTO).companyId = (args.data as FleetListEntryDTO).company?.id;
      (args.data as FleetListEntryDTO).countryCode = this.gridEditSelectedCountryCode ?? CountryISO2.CH;
      this.gridEditSelectedCountryCode = null;
      (args.data as FleetListEntryDTO).controlPlateType = this.gridEditSelectedControlPlateType;
      this.gridEditSelectedControlPlateType = null;
      (args.data as FleetListEntryDTO).type = this.gridEditSelectedType;
      this.gridEditSelectedType = null;
    } else if (args.requestType === SfRequestTypes.Cancel) {
      this.gridEditSelectedCompany = null;
      this.gridEditSelectedCountryCode = null;
      this.gridEditSelectedControlPlateType = null;
      this.gridEditSelectedType = null;
    }
  }

  public override actionComplete(args: SaveEventArgs) {
    if (args.requestType === SfRequestTypes.Save) {
      if (args.data && this.gridEditSelectedCompany) {
        (args.data as FleetListEntryDTO).company = this.gridEditSelectedCompany;
      }
    }
  }

  public beginEdit(args: SaveEventArgs) {
    if (args.requestType === SfRequestTypes.BeginEdit) {
      this.gridEditSelectedCompany = (args.rowData as FleetListEntryDTO).company;

      const code = (args.rowData as FleetListEntryDTO).countryCode;
      if (Object.keys(CountryISO2).includes(code)) {
        this.gridEditSelectedCountryCode = code as CountryISO2;
      } else {
        this.gridEditSelectedCountryCode = CountryISO2.CH; // Default to CH if not found
      }

      this.gridEditSelectedControlPlateType = (args.rowData as FleetListEntryDTO).controlPlateType;
      this.gridEditSelectedType = (args.rowData as FleetListEntryDTO).type;
    }
  }

  public formatControlPlateType(data: FleetListEntryDTO): string {
    return 'i18n.shared.controlPlateType.' + ControlPlateType[data.controlPlateType];
  }

  public formatType(data: FleetListEntryDTO): string {
    return 'i18n.shared.fleet-list-entry-type.' + FleetListEntryType[data.type];
  }

  public getCountries(searchText: string): Observable<AutocompleteItem[]> {
    return of(this.countries.filter(country => country.name.toLowerCase().includes(searchText?.toLowerCase())));
  }

  public controlPlateValidateFn: (args: { [key: string]: string }) => boolean =
    (args: { [key: string]: string }) => {
      let value = args['value'] ?? '';

      value = value.toUpperCase();

      // Regex: only A-Z, ÄÖÜ, 0-9 allowed
      const regex = /^[A-ZÄÖÜ0-9]+$/;
      return regex.test(value);
    };

  protected override initializeEditForm(data: any): void {
  }

  protected override getEditDialogTitleParams(rowData: any) {
    throw new Error('Method not implemented.');
  }
}
